import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { toast } from "sonner";
import { Alert, Box } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom"; 
import { Shift } from "../../components/shiftcard/ShiftCard";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { fetchPendingShifts, PendingShiftsFilter } from "../../redux/PendingShiftsReducer/pendingShiftsSlice";
import { decideShift, ShiftDecision } from "../../redux/DecisionShiftReducer/decisionShiftSlice";
import { fetchSupervisors } from "../../redux/SupervisorReducer/supervisorSlice";
import { fetchGigRequests } from "../../redux/GigRequestsReducer/gigRequestsSlice";
import { ActivityLogEventActions } from "../../constants/activityLogEventActions";
import { useDeepLinkParams } from "../../hooks/useDeepLinkParams";
import { parseShiftId, sortShiftsChronologically } from "../../utils/shiftUtils";
import { gigGroupMatchesDateFilter, groupGigShifts, isGigGroupShowableInApproveReject, mapGigGroupsToShiftCards, mapGigRequestDetailsToRaw } from "../../utils/gigShiftUtils";
import ShiftPageContainer from "../../components/shift-page-container/ShiftPageContainer";

const filterMap: Record<number, PendingShiftsFilter> = {
    0: 'today',
    1: 'all',
    2: 'thisweek',
};

const DECISION_CONFIG: Record<ShiftDecision, { eventActions: string[]; successMsg: string }> = {
    approve: {
        eventActions: [ActivityLogEventActions.ShiftApproved],
        successMsg: "Shift claim approved.",
    },
    reject: {
        eventActions: [ActivityLogEventActions.ShiftDenied, ActivityLogEventActions.ShiftPosted],
        successMsg: "Shift claim rejected. The shift has been re-posted.",
    },
};

// Constants for deep link notification
const DEEP_LINK_ALERT_DURATION = 10000; // 10 seconds
const SCROLL_DELAY = 300; // 300ms

const ApproveRejectPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAppSelector((state) => state.currentUser);
    const { shifts, loading } = useAppSelector((state) => state.pendingShifts);
    const { supervisors, loading: supervisorsLoading } = useAppSelector((state) => state.supervisors);
    const { details: gigRequestDetails } = useAppSelector((state) => state.gigRequests);

    const [selectedTab, setSelectedTab] = useState(0);

    // Group the raw gig request detail rows into gig shift cards. Only show a
    // group in its entirety (never isolate a single day) when at least one of
    // its days matches the selected tab's date range, and only while every
    // day is still Pending or Cancelled (otherwise it should only appear on
    // the calendar).
    const gigShiftGroups = useMemo(
        () => groupGigShifts(mapGigRequestDetailsToRaw(gigRequestDetails))
            .filter((g) => isGigGroupShowableInApproveReject(g))
            .filter((g) => gigGroupMatchesDateFilter(g, filterMap[selectedTab])),
        [gigRequestDetails, selectedTab]
    );

    const mixedPendingShifts = useMemo(
        () => sortShiftsChronologically([
            ...shifts.map((shift) => ({
                ...shift,
                location: shift.location || user?.siteCd || '',
            })),
            ...mapGigGroupsToShiftCards(gigShiftGroups, user?.siteCd || ''),
        ]),
        [shifts, gigShiftGroups, user?.siteCd]
    );

    // Deep link support
    const { hasDeepLink, compositeId, requestId, seqId } = useDeepLinkParams();
    const [highlightedShiftId, setHighlightedShiftId] = useState<string | null>(null);
    const shiftRefs = useRef<Map<string, HTMLDivElement>>(new Map());

    const [selectedSupervisorId, setSelectedSupervisorId] = useState<string>((location.state as any)?.selectedSupervisorId || '');
    const [approveRejectTabs] = useState([
        { label: 'Today', content: '' },
        { label: 'All Shifts', content: '' },
        { label: 'This Week', content: '' }
    ]);

    // Set tab to "All Shifts" if coming from dashboard
    useEffect(() => {
        if ((location.state as any)?.selectedSupervisorId === 'all') {
            setSelectedTab(1);
            // Immediately load the data for all supervisors on the All Shifts tab
            if (user?.siteCd) {
                dispatch(fetchPendingShifts({
                    siteCd: user.siteCd,
                    userId: 'all',
                    filter: 'all',
                    requiredSkillCds: user?.skills ?? [],
                }));
                if (user.userId) {
                    dispatch(fetchGigRequests({
                        siteCd: user.siteCd,
                        userId: user.userId,
                        statusFilter: 'ALL',
                        filter: 'all',
                        isSupervisorId: true,
                    }));
                }
            }
        }
    }, [location.state, dispatch, user?.siteCd, user?.skills, user?.userId]);

    const supervisorOptions = useMemo(() => {
        if (!supervisors || supervisors.length === 0) return [];

        const currentUserEntry = supervisors.find(s => s.workdayId === user?.workdayId);
        const others = supervisors
            .filter(s => s.workdayId !== user?.workdayId)
            .sort((a, b) => {
                const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
                const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
                return nameA.localeCompare(nameB);
            });

        const all = { firstName: 'All', lastName: '', workdayId: 'all' };
        const sorted = currentUserEntry ? [currentUserEntry, ...others] : others;
        return [all, ...sorted].map(s => ({
            label: `${s.firstName} ${s.lastName}`.trim(),
            value: s.workdayId,
        }));
    }, [supervisors, user?.workdayId]);

    const loadGigRequestsForSupervisor = useCallback((siteCd?: string, supervisorId: string = selectedSupervisorId) => {
        const site = siteCd || user?.siteCd;
        if (site && supervisorId) {
            // Always fetch the full set of gig request detail rows (filter: 'all')
            // so that a request spanning multiple days is never split apart by the
            // backend's date filter. Date-range filtering for the selected tab is
            // applied client-side (see gigShiftGroups) so the whole request is
            // shown together whenever any of its days match.
            dispatch(fetchGigRequests({
                siteCd: site,
                userId: supervisorId,
                statusFilter: 'ALL',
                filter: 'all',
                isSupervisorId: true,
            }));
        }
    }, [dispatch, selectedSupervisorId, user?.siteCd]);

    const loadShiftsForTab = useCallback((tabIndex: number, supervisorId?: string, siteCd?: string) => {
        const userId = supervisorId || selectedSupervisorId;
        const site = siteCd || user?.siteCd;
        const filter = filterMap[tabIndex];
        if (site && userId) {
            dispatch(fetchPendingShifts({
                siteCd: site,
                userId,
                filter,
                requiredSkillCds: user?.skills ?? [],
            }));
        }
        loadGigRequestsForSupervisor(site, userId);
    }, [dispatch, loadGigRequestsForSupervisor, user?.skills, selectedSupervisorId, user?.siteCd]);

    const handleTabChange = (tabIndex: number) => {
        setSelectedTab(tabIndex);
        setHighlightedShiftId(null);
        loadShiftsForTab(tabIndex);
    };

    const handleSupervisorChange = (workdayId: string) => {
        setSelectedSupervisorId(workdayId);
        setHighlightedShiftId(null);
        loadShiftsForTab(selectedTab, workdayId);
    };

    const handleApproveRejectConfirm = async (shift: Shift, action: "grab" | "approve" | "reject", rejectionNotes?: string) => {
        if (!user) {
            toast.error("User data not available");
            return;
        }

        if (action !== "approve" && action !== "reject") return;

        const { requestId, requestSeqId } = parseShiftId(shift.id);
        const supervisorName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
        const decision = action as ShiftDecision;
        const config = DECISION_CONFIG[decision];

        if (shift.isGigShift) {
            const gigDetail = gigRequestDetails.find((detail) => String(detail.requestId) === String(shift.requestId ?? requestId));

            try {
                await dispatch(decideShift({
                    decision,
                    payload: {
                        requestId: Number(shift.requestId ?? requestId),
                        requestSeqId: Number(shift.requestSeqId ?? requestSeqId),
                        requestType: 'G',
                        requestSite: user.siteCd,
                        affectedUserId: gigDetail?.requestedByUserId ?? shift.claimedById,
                        affectedUserName: gigDetail?.requestedByUserNm ?? shift.claimedBy ?? shift.postedBy,
                        decidedById: user.workdayId ?? user.userId,
                        decidedByName: supervisorName,
                        isGigRequest: true,
                        rejectionNotes,
                    },
                })).unwrap();

                toast.success(`Gig shift request ${decision === 'approve' ? 'approved' : 'rejected'}.`);
                setHighlightedShiftId(null);
                loadGigRequestsForSupervisor();
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : `Failed to ${action} gig shift`;
                toast.error(errorMessage);
            }
            return;
        }

        // Validate required field
        if (!shift.claimedBy) {
            toast.error("Cannot process: Claimed by user name is missing");
            return;
        }

        try {
            await dispatch(decideShift({
                decision,
                payload: {
                    requestId,
                    requestSeqId,
                    requestType: shift.shiftType,
                    requestSite: user.siteCd,
                    affectedUserId: shift.claimedById,
                    affectedUserName: shift.claimedBy,
                    decidedById: user.workdayId,
                    decidedByName: supervisorName,
                    isGigRequest: false,
                    eventAction: config.eventActions,
                    rejectionNotes,
                },
            })).unwrap();
            
            toast.success(config.successMsg);

            // Clear highlight after action
            setHighlightedShiftId(null);

            // Clear deep link params from URL if this was the highlighted shift
            if (hasDeepLink && compositeId === shift.id) {
                navigate('/approve-reject', { replace: true });
            }

            loadShiftsForTab(selectedTab);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : `Failed to ${action} shift`;
            toast.error(errorMessage);
        }
    };

    // Handle deep link: scroll to and highlight shift
    useEffect(() => {
        // Exit early if no deep link or if still loading initial data
        if (!hasDeepLink || !compositeId) return;
        
        // Wait for data to load before processing deep link
        if (loading || mixedPendingShifts.length === 0 || !selectedSupervisorId) return;

        // Find the shift in the loaded data
        const targetShift = mixedPendingShifts.find(shift => shift.id === compositeId);

        if (!targetShift) {
            toast.error("The requested shift was not found or is no longer pending.", {
                duration: 5000,
            });
            return;
        }

        // Set highlighted shift
        setHighlightedShiftId(compositeId);

        // Scroll to the shift card after a brief delay
        const scrollTimer = setTimeout(() => {
            const shiftElement = shiftRefs.current.get(compositeId);
            if (shiftElement) {
                shiftElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }, SCROLL_DELAY);

        // Auto-remove highlight after duration
        const highlightTimer = setTimeout(() => {
            setHighlightedShiftId(null);
        }, DEEP_LINK_ALERT_DURATION);

        return () => {
            clearTimeout(scrollTimer);
            clearTimeout(highlightTimer);
        };
    }, [hasDeepLink, compositeId, mixedPendingShifts, loading, selectedSupervisorId]);

    useEffect(() => {
        if (user?.siteCd) {
            dispatch(fetchSupervisors(user.siteCd));
        }
    }, [dispatch, user?.siteCd]);

    useEffect(() => {
        if (user?.workdayId && supervisors.length > 0 && !selectedSupervisorId) {
            setSelectedSupervisorId(user.workdayId);
        }
    }, [user?.workdayId, supervisors, selectedSupervisorId]);

    useEffect(() => {
        if (selectedSupervisorId && user?.siteCd) {
            loadShiftsForTab(selectedTab, selectedSupervisorId);
        }
    }, [selectedSupervisorId, selectedTab, loadShiftsForTab, user?.siteCd]);

    return (
        <>
            {/* Deep link info banner */}
            {hasDeepLink && highlightedShiftId && (
                <Box sx={{ px: { xs: 1, sm: 2, md: 3.5, lg: 4, xl: 5 }, pt: 2.5 }}>
                    <Alert
                        severity="info"
                        onClose={() => {
                            setHighlightedShiftId(null);
                            navigate('/approve-reject', { replace: true });
                        }}
                    >
                        A shift has been claimed and requires your review (Request ID: {requestId}-{seqId})
                    </Alert>
                </Box>
            )}

            <ShiftPageContainer
                title="Manage Requests"
                subtitle="View and Manage Shift Requests"
                tabs={approveRejectTabs}
                selectedTab={selectedTab}
                onTabChange={handleTabChange}
                shifts={mixedPendingShifts}
                loading={loading}
                noShiftsMessage="No shifts available"
                filterLabel="Supervisor"
                filterOptions={supervisorOptions}
                filterValue={selectedSupervisorId}
                onFilterChange={handleSupervisorChange}
                filterDisabled={supervisorsLoading}
                onShiftAction={handleApproveRejectConfirm}
                isApproveReject={true}
                highlightedShiftId={highlightedShiftId}
                shiftRefs={shiftRefs}
            />
        </>
    );
};

export default ApproveRejectPage;